import { Routes } from '@angular/router';
import { AppShellComponent } from './layouts/app-shell/app-shell.component';
import { LoginComponent } from './pages/auth/login/login.component';
import { ResetPasswordComponent } from './pages/auth/reset-password/reset-password.component';
import { authChildGuard, authGuard } from './core/guards/auth.guard';
import { permissionGuard } from './core/guards/permission.guard';

export const routes: Routes = [
  { path: 'login', component: LoginComponent },
  { path: 'reset', component: ResetPasswordComponent },
  {
    path: '',
    component: AppShellComponent,
    canActivate: [authGuard],
    canActivateChild: [authChildGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        loadComponent: () => import('./pages/dashboard/dashboard.component').then((m) => m.DashboardComponent),
        data: {}
      },
      {
        path: 'applications/:memberId',
        loadComponent: () => import('./pages/applications/applications.component').then((m) => m.ApplicationsComponent),
        canActivate: [permissionGuard],
        data: { permission: 'MEMBERSHIP_READ' }
      },
      {
        path: 'applications',
        loadComponent: () => import('./pages/applications/applications.component').then((m) => m.ApplicationsComponent),
        canActivate: [permissionGuard],
        data: { permission: 'MEMBERSHIP_READ' }
      },
      {
        path: 'members',
        loadComponent: () => import('./pages/members/members.component').then((m) => m.MembersComponent),
        canActivate: [permissionGuard],
        data: { permission: 'MEMBER_READ' }
      },
      {
        path: 'members/:memberId',
        loadComponent: () => import('./pages/members/member-detail.component').then((m) => m.MemberDetailComponent),
        canActivate: [permissionGuard],
        data: { permission: 'MEMBER_READ' }
      },
      {
        path: 'memberships',
        loadComponent: () => import('./pages/memberships/memberships.component').then((m) => m.MembershipsComponent),
        canActivate: [permissionGuard],
        data: { permission: 'MEMBERSHIP_READ' }
      },
      {
        path: 'memberships/new',
        loadComponent: () => import('./pages/memberships/membership-create.component').then((m) => m.MembershipCreateComponent),
        canActivate: [permissionGuard],
        data: { permission: 'MEMBERSHIP_WRITE' }
      },
      {
        path: 'memberships/:membershipId',
        loadComponent: () => import('./pages/memberships/membership-detail.component').then((m) => m.MembershipDetailComponent),
        canActivate: [permissionGuard],
        data: { permission: 'MEMBERSHIP_READ' }
      },
      {
        path: 'sales',
        loadComponent: () => import('./pages/sales/sales.component').then((m) => m.SalesComponent),
        canActivate: [permissionGuard],
        data: { permission: 'BILLING_READ' }
      },
      {
        path: 'packages',
        loadComponent: () => import('./pages/packages/packages.component').then((m) => m.PackagesComponent),
        canActivate: [permissionGuard],
        data: { permission: 'PACKAGE_READ' }
      },
      {
        path: 'measurements',
        loadComponent: () => import('./pages/measurements/measurements.component').then((m) => m.MeasurementsComponent),
        canActivate: [permissionGuard],
        data: { permission: 'MEASURE_READ' }
      },
      {
        path: 'measurements/:memberId/:measurementId',
        loadComponent: () => import('./pages/measurements/measurement-detail.component').then((m) => m.MeasurementDetailComponent),
        canActivate: [permissionGuard],
        data: { permission: 'MEASURE_READ' }
      },
      { path: 'personal-trainer', redirectTo: 'staff', pathMatch: 'full' },
      { path: 'personal-trainer/create', redirectTo: 'staff/create', pathMatch: 'full' },
      { path: 'personal-trainer/:trainerId', redirectTo: 'staff/:trainerId', pathMatch: 'full' },
      {
        path: 'staff',
        loadComponent: () => import('./pages/staff/staff.component').then((m) => m.StaffComponent),
        canActivate: [permissionGuard],
        data: { permissionAny: ['PT_READ', 'EMPLOYEE_READ'] }
      },
      {
        path: 'staff/create',
        loadComponent: () => import('./pages/staff/staff-create.component').then((m) => m.StaffCreateComponent),
        canActivate: [permissionGuard],
        data: { permissionAny: ['PT_WRITE', 'EMPLOYEE_WRITE'] }
      },
      {
        path: 'staff/:trainerId',
        loadComponent: () => import('./pages/staff/staff-detail.component').then((m) => m.StaffDetailComponent),
        canActivate: [permissionGuard],
        data: { permissionAny: ['PT_READ', 'EMPLOYEE_READ'] }
      },
      {
        path: 'classes',
        loadComponent: () => import('./pages/classes/classes.component').then((m) => m.ClassesComponent),
        canActivate: [permissionGuard],
        data: { permission: 'CLASS_READ' }
      },
      {
        path: 'reports',
        loadComponent: () => import('./pages/reports/reports.component').then((m) => m.ReportsComponent),
        canActivate: [permissionGuard],
        data: { permission: 'REPORT_READ' }
      },
      {
        path: 'masters',
        loadComponent: () => import('./pages/masters/masters.component').then((m) => m.MastersComponent),
        canActivate: [permissionGuard],
        data: { permission: 'SETTINGS_READ' }
      },
      {
        path: 'masters/:masterKey',
        loadComponent: () => import('./pages/masters/masters.component').then((m) => m.MastersComponent),
        canActivate: [permissionGuard],
        data: { permission: 'SETTINGS_READ' }
      },
      {
        path: 'content-studio',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      },
      {
        path: 'content-studio/:path',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      },
      {
        path: 'tenant-settings',
        loadComponent: () => import('./pages/tenant-settings/tenant-settings.component').then((m) => m.TenantSettingsComponent),
        canActivate: [permissionGuard],
        data: { permission: 'SETTINGS_READ' }
      },
      {
        path: 'branch-settings',
        loadComponent: () => import('./pages/branch-settings/branch-settings.component').then((m) => m.BranchSettingsComponent),
        canActivate: [permissionGuard],
        data: { permission: 'SETTINGS_READ' }
      },
      {
        path: 'roles-trials',
        loadComponent: () => import('./pages/roles-trials/roles-trials.component').then((m) => m.RolesTrialsComponent),
        canActivate: [permissionGuard],
        data: { permission: 'SETTINGS_READ' }
      },
      {
        path: 'audit-logs',
        loadComponent: () => import('./pages/audit-logs/audit-logs.component').then((m) => m.AuditLogsComponent),
        canActivate: [permissionGuard],
        data: { permission: 'AUDIT_READ' }
      },
      {
        path: 'notifications',
        loadComponent: () => import('./pages/notification-center/notification-center.component').then((m) => m.NotificationCenterComponent),
        canActivate: [permissionGuard],
        data: { permission: 'SETTINGS_READ' }
      },
      {
        path: 'access-control',
        loadComponent: () => import('./pages/access-control/access-control.component').then((m) => m.AccessControlComponent),
        canActivate: [permissionGuard],
        data: { permission: 'SETTINGS_READ' }
      },
      {
        path: 'checkin-logs',
        loadComponent: () => import('./pages/checkin-logs/checkin-logs.component').then((m) => m.CheckinLogsComponent),
        data: {}
      },
      {
        path: 'notifications-module',
        loadComponent: () => import('./pages/notifications/notifications.component').then((m) => m.NotificationsComponent),
        canActivate: [permissionGuard],
        data: { permission: 'SETTINGS_READ' },
        children: [
          { path: '', redirectTo: 'groups', pathMatch: 'full' },
          {
            path: 'groups',
            loadComponent: () => import('./pages/notifications/notifications-groups.component').then((m) => m.NotificationsGroupsComponent),
            data: {}
          },
          {
            path: 'templates',
            loadComponent: () => import('./pages/notifications/notifications-templates.component').then((m) => m.NotificationsTemplatesComponent),
            data: {}
          },
          {
            path: 'compose',
            loadComponent: () => import('./pages/notifications/notifications-compose.component').then((m) => m.NotificationsComposeComponent),
            data: {}
          },
          {
            path: 'deliveries',
            loadComponent: () => import('./pages/notifications/notifications-deliveries.component').then((m) => m.NotificationsDeliveriesComponent),
            data: {}
          },
          {
            path: 'inbox',
            loadComponent: () => import('./pages/notifications/notifications-inbox.component').then((m) => m.NotificationsInboxComponent),
            data: {}
          },
          {
            path: 'testing',
            loadComponent: () => import('./pages/notifications/notifications-testing.component').then((m) => m.NotificationsTestingComponent),
            data: {}
          }
        ]
      },
      { path: 'design-system', redirectTo: 'dashboard', pathMatch: 'full' }
    ]
  },
  { path: '**', redirectTo: 'login' }
];
